import React, { ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';
import { useTheme } from './ThemeProvider';

interface MuiThemeProps {
  children: ReactNode;
}

const MuiTheme: React.FC<MuiThemeProps> = ({ children }) => {
  const { colors, isDarkMode } = useTheme();

  // Create MUI theme using our theme context colors
  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: colors.primary.main,
        light: colors.primary.light,
        dark: colors.primary.dark,
        contrastText: colors.text.light,
      },
      secondary: {
        main: colors.secondary.main,
        light: colors.secondary.light,
        dark: colors.secondary.dark,
        contrastText: colors.text.light,
      },
      error: {
        main: colors.danger,
        light: colors.score.high,
        dark: colors.score.critical,
      },
      warning: {
        main: colors.warning,
        light: colors.score.moderate,
        dark: colors.score.high,
      },
      info: {
        main: colors.info,
        light: colors.score.none,
        dark: colors.secondary.main,
      },
      success: {
        main: colors.success,
        light: colors.score.none,
        dark: '#7a9e90',
      },
      background: {
        default: colors.background.alt,
        paper: colors.background.default,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
      },
      divider: colors.border,
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: {
        fontSize: '2.5rem',
        fontWeight: 500,
        color: colors.text.primary,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 500,
        color: colors.text.primary,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 500,
        color: colors.text.primary,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 500,
        color: colors.text.primary,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 500,
        color: colors.text.primary,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 500,
        color: colors.text.primary,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.5,
        color: colors.text.primary,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
        color: colors.text.primary,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            },
          },
          outlined: {
            borderColor: colors.border,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0px 1px 5px rgba(0, 0, 0, 0.05)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${colors.border}`,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            backgroundColor: colors.border,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: `${colors.primary.main}10`,
              '&:hover': {
                backgroundColor: `${colors.primary.main}20`,
              },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            backgroundColor: colors.background.alt,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
          },
        },
      },
    },
  });

  return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>;
};

export default MuiTheme; 